/**
 * WorkspaceBuilderPage — P16.6
 *
 * Multi-step wizard for coaches to scaffold a new domain pack via the
 * TeachIQ authoring chat endpoint.
 *
 * Step 1 — Domain Identity
 * Step 2 — Domain Configuration
 * Step 3 — Review & Create
 */
import React, { useState, useRef, useEffect } from 'react';
import CoachLayout from './CoachLayout';

const DIFFICULTY_OPTIONS = ['beginner', 'intermediate', 'advanced'];
const SCENE_TYPE_OPTIONS  = ['slide_deck', 'video', 'quiz', 'capstone', 'code_lab'];

function getToken() {
  return localStorage.getItem('scenario_os_token') || '';
}

// ── Shared wizard UI ──────────────────────────────────────────────────────────

function StepIndicator({ step }) {
  const steps = ['Identity', 'Configuration', 'Review & Create'];
  return (
    <div style={{ display: 'flex', gap: 0, marginBottom: 32 }}>
      {steps.map((label, i) => {
        const idx = i + 1;
        const done = step > idx;
        const active = step === idx;
        return (
          <React.Fragment key={idx}>
            {i > 0 && (
              <div style={{
                flex: 1, height: 2, background: done ? '#4f46e5' : '#e5e7eb',
                marginTop: 16, transition: 'background 0.3s',
              }} />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: done ? '#4f46e5' : active ? '#4f46e5' : '#e5e7eb',
                color: done || active ? '#fff' : '#9ca3af',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14, transition: 'background 0.3s',
              }}>
                {done ? '✓' : idx}
              </div>
              <div style={{ fontSize: 12, color: active ? '#4f46e5' : '#6b7280', fontWeight: active ? 600 : 400, whiteSpace: 'nowrap' }}>
                {label}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FieldLabel({ children, required }) {
  return (
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
      {children}{required && <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 7,
  border: '1px solid #d1d5db',
  fontSize: 14,
  boxSizing: 'border-box',
  color: '#111827',
  background: '#f9fafb',
};

function CheckboxGroup({ options, selected, onChange }) {
  const toggle = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter((x) => x !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <label
            key={opt}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
              padding: '5px 12px', borderRadius: 6,
              border: `1px solid ${active ? '#4f46e5' : '#d1d5db'}`,
              background: active ? '#eef2ff' : '#fff',
              fontSize: 13, fontWeight: active ? 600 : 400, color: active ? '#4f46e5' : '#374151',
              transition: 'all 0.15s',
            }}
          >
            <input
              type="checkbox"
              checked={active}
              onChange={() => toggle(opt)}
              style={{ display: 'none' }}
            />
            {active ? '✓ ' : ''}{opt}
          </label>
        );
      })}
    </div>
  );
}

function NavButtons({ step, onBack, onNext, nextLabel = 'Next', disabled = false }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
      {step > 1 ? (
        <button onClick={onBack} style={{
          padding: '9px 22px', borderRadius: 7, border: '1px solid #d1d5db',
          background: '#fff', color: '#374151', fontSize: 14, cursor: 'pointer', fontWeight: 500,
        }}>
          ← Back
        </button>
      ) : <div />}
      <button
        onClick={onNext}
        disabled={disabled}
        style={{
          padding: '9px 24px', borderRadius: 7, border: 'none',
          background: disabled ? '#e5e7eb' : '#4f46e5',
          color: disabled ? '#9ca3af' : '#fff',
          fontSize: 14, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
          transition: 'background 0.15s',
        }}
      >
        {nextLabel}
      </button>
    </div>
  );
}

// ── Step 1: Domain Identity ────────────────────────────────────────────────────

function Step1({ form, onChange, onNext }) {
  const valid = form.domainId.trim() && form.displayName.trim();
  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 18, color: '#1f2937' }}>Domain Identity</h2>

      <div style={{ marginBottom: 18 }}>
        <FieldLabel required>Domain ID</FieldLabel>
        <input
          type="text"
          value={form.domainId}
          onChange={(e) => onChange('domainId', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
          placeholder="e.g. product_management"
          style={inputStyle}
        />
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Lowercase letters and underscores only</div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <FieldLabel required>Display Name</FieldLabel>
        <input
          type="text"
          value={form.displayName}
          onChange={(e) => onChange('displayName', e.target.value)}
          placeholder="e.g. Product Management"
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 18 }}>
        <FieldLabel>Description</FieldLabel>
        <textarea
          value={form.description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder="Brief description of this domain's purpose and target audience…"
          rows={4}
          style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit' }}
        />
      </div>

      <div style={{ marginBottom: 18 }}>
        <FieldLabel>Primary Color</FieldLabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type="color"
            value={form.primaryColor}
            onChange={(e) => onChange('primaryColor', e.target.value)}
            style={{ width: 44, height: 36, borderRadius: 6, border: '1px solid #d1d5db', cursor: 'pointer', padding: 2 }}
          />
          <input
            type="text"
            value={form.primaryColor}
            onChange={(e) => onChange('primaryColor', e.target.value)}
            style={{ ...inputStyle, width: 120 }}
          />
        </div>
      </div>

      <NavButtons step={1} onNext={onNext} disabled={!valid} />
    </div>
  );
}

// ── Step 2: Domain Configuration ─────────────────────────────────────────────

function Step2({ form, onChange, onNext, onBack }) {
  const valid = form.difficulties.length > 0 && form.sceneTypes.length > 0;
  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 18, color: '#1f2937' }}>Domain Configuration</h2>

      <div style={{ marginBottom: 24 }}>
        <FieldLabel required>Difficulty Levels</FieldLabel>
        <CheckboxGroup
          options={DIFFICULTY_OPTIONS}
          selected={form.difficulties}
          onChange={(v) => onChange('difficulties', v)}
        />
      </div>

      <div style={{ marginBottom: 24 }}>
        <FieldLabel required>Scene Types to Enable</FieldLabel>
        <CheckboxGroup
          options={SCENE_TYPE_OPTIONS}
          selected={form.sceneTypes}
          onChange={(v) => onChange('sceneTypes', v)}
        />
      </div>

      <NavButtons step={2} onBack={onBack} onNext={onNext} disabled={!valid} />
    </div>
  );
}

// ── Step 3: Review & Create ───────────────────────────────────────────────────

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ width: 160, fontSize: 13, fontWeight: 600, color: '#6b7280', flexShrink: 0 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#1f2937', flex: 1 }}>{value}</div>
    </div>
  );
}

function Step3({ form, onBack, onSubmit, creating, log, success, error }) {
  const logRef = useRef(null);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [log]);

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 18, color: '#1f2937' }}>Review & Create</h2>

      <div style={{
        background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb',
        padding: '4px 16px', marginBottom: 24,
      }}>
        <SummaryRow label="Domain ID" value={<code style={{ background: '#e5e7eb', padding: '1px 5px', borderRadius: 3 }}>{form.domainId}</code>} />
        <SummaryRow label="Display Name" value={form.displayName} />
        <SummaryRow label="Description" value={form.description || <span style={{ color: '#9ca3af' }}>None</span>} />
        <SummaryRow label="Primary Color" value={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 16, height: 16, borderRadius: 3, background: form.primaryColor, border: '1px solid #d1d5db', display: 'inline-block' }} />
            {form.primaryColor}
          </span>
        } />
        <SummaryRow label="Difficulty Levels" value={form.difficulties.join(', ')} />
        <SummaryRow label="Scene Types" value={form.sceneTypes.join(', ')} />
      </div>

      {success && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8,
          padding: '14px 16px', marginBottom: 16, color: '#15803d', fontSize: 14, fontWeight: 600,
        }}>
          Domain scaffold initiated! The agent is building your domain pack.
        </div>
      )}

      {log.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Agent Progress</div>
          <div
            ref={logRef}
            style={{
              background: '#0f172a', color: '#94a3b8', borderRadius: 8,
              padding: 14, fontSize: 12, lineHeight: 1.7, maxHeight: 200, overflowY: 'auto',
              fontFamily: 'monospace',
            }}
          >
            {log.map((line, i) => (
              <div key={i} style={{ whiteSpace: 'pre-wrap' }}>{line}</div>
            ))}
            {creating && <div style={{ color: '#fbbf24' }}>▌</div>}
          </div>
        </div>
      )}

      {error && (
        <div style={{ color: '#dc2626', fontSize: 13, marginTop: 12, padding: '8px 12px', background: '#fef2f2', borderRadius: 6 }}>
          {error}
        </div>
      )}

      <NavButtons
        step={3}
        onBack={onBack}
        onNext={onSubmit}
        nextLabel={creating ? 'Creating…' : success ? 'Done' : 'Create Domain'}
        disabled={creating || success}
      />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const INITIAL_FORM = {
  domainId: '',
  displayName: '',
  description: '',
  primaryColor: '#6366f1',
  difficulties: ['beginner', 'intermediate', 'advanced'],
  sceneTypes: ['slide_deck', 'quiz', 'video'],
};

export default function WorkspaceBuilderPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [creating, setCreating] = useState(false);
  const [log, setLog] = useState([]);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleCreate = async () => {
    if (creating || success) return;
    setCreating(true);
    setLog([]);
    setError('');

    const message = `Create a new domain pack for: ${form.domainId} — ${form.displayName}. Description: ${form.description}. Difficulty: ${form.difficulties.join(',')}. Scene types: ${form.sceneTypes.join(',')}.`;

    const token = getToken();
    try {
      const resp = await fetch('/scenario-os/authoring/chat/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ domain_id: 'ai', message }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: resp.statusText }));
        throw new Error(err?.detail || `HTTP ${resp.status}`);
      }

      const contentType = resp.headers.get('content-type') || '';
      if (contentType.includes('text/event-stream') || contentType.includes('text/plain')) {
        // SSE / streaming response
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n');
          buffer = parts.pop();
          for (const line of parts) {
            const trimmed = line.trim();
            if (!trimmed || trimmed === ':') continue;
            if (trimmed.startsWith('data:')) {
              const payload = trimmed.slice(5).trim();
              if (payload === '[DONE]') continue;
              try {
                const obj = JSON.parse(payload);
                const text = obj.content || obj.text || obj.delta || JSON.stringify(obj);
                setLog((prev) => [...prev, text]);
              } catch {
                setLog((prev) => [...prev, payload]);
              }
            } else {
              setLog((prev) => [...prev, trimmed]);
            }
          }
        }
      } else {
        const data = await resp.json();
        const text = data.content || data.response || data.message || JSON.stringify(data);
        setLog([text]);
      }
      setSuccess(true);
    } catch (e) {
      setError(e.message || 'Failed to create domain scaffold.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <CoachLayout title="Domain Builder">
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        <StepIndicator step={step} />
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
          padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          {step === 1 && (
            <Step1
              form={form}
              onChange={setField}
              onNext={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <Step2
              form={form}
              onChange={setField}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
            />
          )}
          {step === 3 && (
            <Step3
              form={form}
              onBack={() => setStep(2)}
              onSubmit={handleCreate}
              creating={creating}
              log={log}
              success={success}
              error={error}
            />
          )}
        </div>
      </div>
    </CoachLayout>
  );
}
